import { ComponentProps, ComponentPropsWithoutRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Slider from 'react-slick';
import { Button } from '@givebutter/primitives/button';
import { useMediaQuery } from '@givebutter/react-util';
import { faArrowRight } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as GivebutterPlusTrialDialog from 'assets/js/pages/account-settings/account-about/givebutter-plus/GivebutterPlusTrialDialog';
import * as GivebutterPlusWelcomeDialog from 'assets/js/pages/account-settings/account-about/givebutter-plus/GivebutterPlusWelcomeDialog';
import { MobileAppBadges } from 'components/mobile-app-badges';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import CardReaderResponsive from 'images/card-reader-responsive.webp';
import CardReader from 'images/card-reader.webp';
import DarkerGrainBg from 'images/gbp-grain-darker.png';
import PlusCalloutImg from 'images/gbp-home-callout.webp';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import MobileAppResponsive from 'images/mobile-app-responsive.webp';
import MobileApp from 'images/mobile-app.webp';
import WebsiteEmbeds from 'images/website-embeds.webp';
import { twMerge } from 'tailwind-merge';

// Can't just inject the pixel height since tailwind doesn't compile dynamic classes
const CAROUSEL_HEIGHT_CLASS = 'h-[400px]';
const CAROUSEL_MEDIA_QUERY = 639;

const Callouts = () => {
    const { hasGivebutterPlusAccess } = useCurrentAccount();
    const showPlusCard = !hasGivebutterPlusAccess;
    const [isWelcomeDialogOpen, setIsWelcomeDialogOpen] = useState(false);
    const isMobile = useMediaQuery(`(max-width: ${CAROUSEL_MEDIA_QUERY}px)`);

    return (
        <>
            {isMobile ? (
                <Slider
                    className={`mb-8 ${CAROUSEL_HEIGHT_CLASS} [&>.slick-dots_li_button]:before:text-[12px] [&>.slick-dots_li_button]:before:content-['•']`}
                    dots={true}
                    slidesToShow={1}
                >
                    <div>
                        {showPlusCard ? (
                            <PlusCard
                                onSuccess={() => setIsWelcomeDialogOpen(true)}
                                className={`${CAROUSEL_HEIGHT_CLASS}`}
                            />
                        ) : (
                            <MobileAppCard className={`${CAROUSEL_HEIGHT_CLASS}`} />
                        )}
                    </div>
                    <div>
                        {showPlusCard ? (
                            <MobileAppCard className={`${CAROUSEL_HEIGHT_CLASS}`} />
                        ) : (
                            <StripeReaderCard className={`${CAROUSEL_HEIGHT_CLASS}`} />
                        )}
                    </div>
                    <div>
                        <WidgetsCard className={`${CAROUSEL_HEIGHT_CLASS}`} />
                    </div>
                </Slider>
            ) : (
                <div className={'grid grid-cols-12 gap-3 rounded-2xl bg-gray-100 p-3'}>
                    <div className={'col-span-7 flex flex-col gap-3'}>
                        <div className={'flex-1'}>
                            {showPlusCard ? (
                                <PlusCard onSuccess={() => setIsWelcomeDialogOpen(true)} />
                            ) : (
                                <MobileAppCard />
                            )}
                        </div>
                        <div className={'flex-1'}>
                            {showPlusCard ? <MobileAppCard /> : <StripeReaderCard />}
                        </div>
                    </div>
                    <div className={'col-span-5 [&>div]:h-full'}>
                        <WidgetsCard />
                    </div>
                </div>
            )}
            <GivebutterPlusWelcomeDialog.Root
                open={isWelcomeDialogOpen}
                onOpenChange={open => {
                    setIsWelcomeDialogOpen(open);
                }}
            />
        </>
    );
};

const MobileAppCard = (props: ComponentProps<'div'>) => {
    const { className = '', ...rest } = props;
    const isMobile = useMediaQuery(`(max-width: ${CAROUSEL_MEDIA_QUERY}px)`);

    return (
        <div
            className={`grid grid-cols-8 gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}
            {...rest}
        >
            <div
                className={
                    'relative col-span-8 h-[200px] rounded-xl bg-[url("images/callouts-gradient.png")] bg-cover bg-no-repeat sm:order-2 sm:col-span-3 sm:-my-6 sm:-mr-6 sm:h-auto sm:rounded-bl-none sm:rounded-br-xl sm:rounded-tl-none sm:rounded-tr-xl sm:bg-none'
                }
            >
                <img
                    className={
                        'pointer-events-none absolute bottom-0 left-[35%] h-[85%] rounded-br-xl sm:left-auto sm:right-0 sm:h-auto'
                    }
                    src={isMobile ? MobileAppResponsive : MobileApp}
                    alt={'Givebutter Mobile App'}
                />
            </div>
            <div className={'col-span-8 flex flex-col gap-y-6 sm:col-span-5'}>
                <div className={'flex flex-col gap-y-1'}>
                    <p className={'text-lg font-medium leading-snug'}>Download the mobile app</p>
                    <p className={'text-sm text-gray-500'}>
                        Bring the power of Givebutter with you on the go. Collect donations, scan
                        tickets, and more at all of your in-person fundraising initiatives.
                    </p>
                </div>
                <div>
                    <MobileAppBadges />
                </div>
            </div>
        </div>
    );
};

const StripeReaderCard = (props: ComponentProps<'div'>) => {
    const { readerUrl } = useCurrentAccount();
    const { className = '', ...rest } = props;
    const isMobile = useMediaQuery(`(max-width: ${CAROUSEL_MEDIA_QUERY}px)`);

    return (
        <div
            className={`grid grid-cols-12 flex-col gap-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm ${className}`}
            {...rest}
        >
            <div
                className={
                    'relative col-span-12 h-[215px] rounded-xl bg-[url("images/callouts-gradient.png")] bg-cover bg-no-repeat sm:col-span-5 sm:-my-6 sm:-ml-6 sm:h-auto sm:rounded-bl-xl sm:rounded-br-none sm:rounded-tl-xl sm:rounded-tr-none sm:bg-none'
                }
            >
                <img
                    className={'h-full rounded-bl-xl sm:absolute sm:bottom-0 sm:left-0 sm:h-auto'}
                    src={isMobile ? CardReaderResponsive : CardReader}
                    alt={'Stripe Card Reader'}
                />
            </div>
            <div className={'col-span-12 flex flex-col gap-y-1 sm:col-span-7'}>
                <p className={'text-lg font-medium leading-snug'}>Need the card reader?</p>
                <p className={'text-sm text-gray-500'}>
                    To securely accept and process credit card donations with the Mobile App you'll
                    need the Stripe M2 Reader.
                </p>
                <a
                    className={'flex items-center gap-x-1 text-blue-500'}
                    href={readerUrl ?? undefined}
                >
                    Purchase Card Reader
                    <FontAwesomeIcon className={'text-blue-500'} icon={faArrowRight} />
                </a>
            </div>
        </div>
    );
};

const WidgetsCard = (props: ComponentProps<'div'>) => {
    const { className = '', ...rest } = props;
    const { accountId } = useCurrentAccount();

    return (
        <div
            className={`flex flex-col gap-y-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-col-reverse sm:justify-end ${className}`}
            {...rest}
        >
            <div
                className={'flex flex-grow items-center justify-center rounded-xl bg-gray-100 p-4'}
            >
                <img
                    className={'h-auto max-h-[215px]'}
                    src={WebsiteEmbeds}
                    alt={'Givebutter Embeds'}
                />
            </div>
            <div className={'flex flex-col gap-y-1'}>
                <p className={'text-lg font-medium leading-snug'}>Website Widgets</p>
                <p className={'text-sm text-gray-500'}>
                    Make it easy for your supporters to contribute to your cause by adding a
                    customized donation button directly on your website.
                </p>
                <Link
                    to={`/accounts/${accountId}/settings/integrations/widgets`}
                    className={'flex items-center gap-x-1 text-blue-500'}
                >
                    Get Started
                    <FontAwesomeIcon className={'text-blue-500'} icon={faArrowRight} />
                </Link>
            </div>
        </div>
    );
};

interface PlusCardProps extends ComponentPropsWithoutRef<'div'> {
    onSuccess?: () => void;
}
const PlusCard = (props: PlusCardProps) => {
    const { onSuccess, className = '', ...rest } = props;
    return (
        <div
            className={twMerge(
                'flex flex-col overflow-clip rounded-xl border border-gray-200 bg-white p-6 shadow-sm sm:flex-row sm:p-0',
                className,
            )}
            {...rest}
        >
            <div
                className="flex w-full items-center justify-center rounded-xl p-2 sm:max-w-44 sm:rounded-none"
                style={{
                    background: `url("${DarkerGrainBg}") repeat`,
                }}
            >
                <img
                    src={PlusCalloutImg}
                    alt="Givebutter Plus example workflow"
                    className={'h-auto max-h-48'}
                />
            </div>
            <div className="flex flex-col gap-2 px-4 py-6">
                <img
                    src={GivebutterPlusLogo}
                    className="w-full max-w-[9.375rem]"
                    alt="Givebutter Plus logo"
                />
                <div className="text-sm text-gray-600">
                    Unlock your fundraising potential with advanced automation, deeper donor
                    insights, and supporter engagement tools.
                </div>
                <GivebutterPlusTrialDialog.Root onSuccess={() => onSuccess?.()}>
                    <GivebutterPlusTrialDialog.Trigger asChild>
                        <Button variant="none" color="primary" size="md" className="font-medium">
                            Start free trial
                            <FontAwesomeIcon className={'text-blue-500'} icon={faArrowRight} />
                        </Button>
                    </GivebutterPlusTrialDialog.Trigger>
                </GivebutterPlusTrialDialog.Root>
            </div>
        </div>
    );
};

export { Callouts };
